import React, { useCallback, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Dashboard from './Pages/Feels/Dashboard/Dashboard'
import Data from './Pages/Data'
import NotFound from './Pages/Helpers/404'
import CreateNewFeelStep1 from './Pages/Feels/createNewFeelStep1'
import Nav from './Components/Navbar'
import SideBar from './Components/Sidebar'
import LoginPage from './Pages/Authentication/Login'
import OrgLoginPage from './Pages/Authentication/OrgLoginPage'
import LoginByInvitePage from './Pages/Authentication/LoginByInvitePage'

import PrivateRoute from './PrivateRoute'
import AllArtists from './Pages/Artists/AllArtists/AllArtists'
import Artist from './Pages/Artists/Artist/Artist'
import Albums from './Pages/Artists/Albums/Albums'
import FeelsForSong from './Pages/Songs/FeelsForSong/FeelsForSong'
import Feel from './Pages/Feels/Feel/Feel'
import EditSong from './Pages/Songs/EditSong/EditSong'
import ArtistSongs from './Pages/Songs/ArtistSongs/ArtistSongs'
import AllSongs from './Pages/Songs/AllSongs/AllSongs'
import AlbumSongs from './Pages/Songs/AlbumSongs/AlbumSongs'
import './index.css'
import SongDetails from './Pages/Songs/SongDetails/SongDetails'
import { isEmailInSongManagementTeam } from './Helpers/RestrictedEmailsArray'
import Trivia from './Pages/Trivia/Trivia'
import { useAuth } from './Contexts/AuthContext'
import trackPathForAnalytics from './firebase-analytics'

import Collections from './Pages/Collections/Collections'
import CollectionDetail from './Pages/Collections/CollectionDetail'
import ExportFeel from './Pages/Feels/ExportFeel/ExportFeel'
import { Filters } from './Pages/Filters/Filters'
import { Backdrop } from './Pages/Songs/Backdrop/Backdrop'
import ArtistFeels from './Pages/Feels/ArtistFeels/ArtistFeels'
import { Step2 } from './Pages/Feels/CreateFeel/Step2'
import Step1 from './Pages/Feels/CreateFeel/Step1'
import useAdminAccess from './services/hooks/useAdminAccess'
import OrganizationUsers from './Pages/Organization/OrganizationUsers'
import { permissions } from './constants/roles'
import useReadOnly from './services/hooks/useReadOnly'
import CreateTease from './Pages/Teases/CreateTease'
import TeaseDetails from './Pages/Teases/TeaseDetails'
import EditTease from './Pages/Teases/EditTease'
import Organizations from './Pages/Organizations'
import OrganizationDetails from './Pages/Organizations/OrganizationDetails'
import TeamDetails from './Pages/Organizations/TeamDetails'
import OrganizationTeams from './Pages/Organizations/OrganizationTeams'
import OrganizationArtists from './Pages/Organizations/OrganizationArtists'
import TeamDetailsMembers from './Pages/Organizations/TeamDetailsMembers'
import OrganizationMembers from './Pages/Organizations/OrganizationMembers'
import TeamDetailsArtists from './Pages/Organizations/TeamDetailsArtists'
import MyTeams from './Pages/Organizations/MyTeams'
import OnlyInternalRoute from './protectedRoutes/OnlyInternalRoute'
import OrganizationRoute from './protectedRoutes/OrganizationRoute'
import OrganizationGroupRoute from './protectedRoutes/OrganizationGroupRoute'
import Reports from './Pages/Reports/Reports'
import FeelsShuffle from './Pages/Feels/FeelsShuffle'
import FeelsReview from './Pages/Feels/FeelsReview'
import FeelsReviewByCollection from './Pages/Feels/FeelsReviewByCollection'
import ResetPasswordPage from './Pages/Authentication/ResetPasswordPage'
import PitchPage from './Pages/Pitch/Pitch'
import AllReports from './Pages/AllReports'
import ExternalRedirect from './Components/ExternalRedirect'
import Codes from './Pages/Codes/Codes'
import CookiePolicy from './Pages/CookiePolicy/CookiePolicy'
import FeaturedCollectionUpdate from './Pages/FeaturedCollectionUpdate/FeaturedCollectionUpdate'
import IsrcExceptionReview from './Pages/Songs/IsrcExceptionReview/IsrcExceptionReview'
import FeelsSyncJobs from './Pages/FeelsSyncJobs/FeelsSyncJobs'
import IngestWorkers from './Pages/Ingest/Workers'
import IngestStatus from './Pages/IngestStatus/IngestStatus'
import RequestDetail from './Pages/IngestStatus/RequestDetail'
import IngestRoute from './protectedRoutes/IngestRoute'
import FeelsDrawerPreview from './Pages/Feels/FeelsDrawerPreview'
import ScreamRoom from './Pages/Feels/ScreamRoom'

export default function Router() {
  const { getCurrentUser } = useAuth()
  const { hasFullAdminAccess, isAdmin } = useAdminAccess()
  const { isReadOnly: isSongReadOnly } = useReadOnly(permissions.r.music_song)
  const { isReadOnly: isFeelsFactoryReadOnly } = useReadOnly(
    permissions.r.feels_factory
  )
  const { isReadOnly: isFeelsReadOnly } = useReadOnly(permissions.r.feels_mm)

  const { pathname, search } = useLocation()

  const analytics = useCallback(() => {
    trackPathForAnalytics({
      path: pathname,
      search: search,
      title: pathname.split('/')[1],
    })
  }, [pathname, search])

  useEffect(() => {
    if (!pathname.includes('/reports')) {
      localStorage.setItem('prevPageUrl', window.location.href)
      localStorage.setItem(
        'prevPageTitle',
        document.title || pathname.split('/').pop() || 'Recently Visited Item'
      )
    }
  }, [pathname])
  useEffect(() => {
    analytics()
  }, [analytics])

  return (
    <>
      {getCurrentUser() && <SideBar />}
      <div id="main" style={!getCurrentUser() ? { padding: 0, margin: 0 } : {}}>
        {getCurrentUser() && <Nav />}
        {isEmailInSongManagementTeam(getCurrentUser()?.email) ? (
          <div className="child-wrapper">
            <Routes>
              <Route
                path="/login/invite"
                element={
                  getCurrentUser() ? (
                    <Navigate to="artists" />
                  ) : (
                    <LoginByInvitePage />
                  )
                }
              />

              <Route
                path="/reset-password"
                element={
                  getCurrentUser() ? (
                    <Navigate to="artists" />
                  ) : (
                    <ResetPasswordPage />
                  )
                }
              />

              <Route
                path="/login/admin"
                element={
                  getCurrentUser() ? <Navigate to="/artists" /> : <LoginPage />
                }
              />
              <Route
                path="/login"
                element={
                  getCurrentUser() ? <Navigate to="/artists" /> : <LoginPage />
                }
              />
              <Route path="/" element={<PrivateRoute />}>
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route
                  path="/dashboard"
                  element={
                    <OnlyInternalRoute>
                      <AllSongs />
                    </OnlyInternalRoute>
                  }
                />
                <Route
                  path="/songs/all"
                  element={<Navigate to="/dashboard" />}
                />
                <Route path="/pitch" element={<PitchPage />} />
                <Route path="/codes" element={<Codes />} />

                {!isSongReadOnly && (
                  <Route path="/edit-song/:id" element={<EditSong />} />
                )}
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        ) : (
          <div className="child-wrapper">
            <Routes>
              <Route
                path="/login/invite"
                element={
                  getCurrentUser() ? (
                    <Navigate to="/artists" />
                  ) : (
                    <LoginByInvitePage />
                  )
                }
              />

              <Route
                path="/reset-password"
                element={
                  getCurrentUser() ? (
                    <Navigate to="artists" />
                  ) : (
                    <ResetPasswordPage />
                  )
                }
              />

              <Route
                path="/login/admin"
                element={
                  getCurrentUser() ? <Navigate to="/artists" /> : <LoginPage />
                }
              />
              <Route
                path="/login"
                element={
                  getCurrentUser() ? (
                    <Navigate to="/artists" />
                  ) : (
                    <OrgLoginPage />
                  )
                }
              />

              <Route path="/" element={<PrivateRoute />}>
                <Route path="/" element={<Navigate to="/artists" />} />
                <Route
                  path="/dashboard"
                  element={
                    <OnlyInternalRoute>
                      <Dashboard />
                    </OnlyInternalRoute>
                  }
                />
                <Route
                  path="/feelsyncjobs"
                  element={
                    <OnlyInternalRoute>
                      <FeelsSyncJobs />
                    </OnlyInternalRoute>
                  }
                />
                <Route path="/ingest" element={<IngestRoute><IngestStatus /></IngestRoute>} />
                <Route path="/ingest/workers" element={<IngestRoute><IngestWorkers /></IngestRoute>} />
                <Route path="/ingest/:id" element={<IngestRoute><RequestDetail /></IngestRoute>} />

                {hasFullAdminAccess && (
                  <>
                    <Route path="/collections" element={<Collections />} />
                    <Route path="/collections/:id" element={<CollectionDetail />} />
                    <Route path="/filters" element={<Filters />} />
                    <Route path="/feels-shuffle" element={<FeelsShuffle />} />
                    <Route path="/feels-review" element={<FeelsReview />} />
                    <Route path="/feels-review-by-collection" element={<FeelsReviewByCollection />} />
                    <Route path="/all-reports" element={<AllReports />} />
                    <Route path="/featured-collection-update" element={<FeaturedCollectionUpdate />} />
                    <Route
                      path="/songs/isrc-review"
                      element={
                        <OnlyInternalRoute>
                          <IsrcExceptionReview />
                        </OnlyInternalRoute>
                      }
                    />
                  </>
                )}
                {isAdmin && (
                  <Route
                    path="/organization"
                    element={
                      // <OrganizationRoute>
                      <OrganizationUsers />
                      // </OrganizationRoute>
                    }
                  />
                )}
                <Route path="/artists" element={<AllArtists />} />
                <Route path="/artist/:artist_name" element={<Artist />} />
                <Route path="/songs/all" element={<AllSongs />} />
                <Route path="/pitch" element={<PitchPage />} />
                <Route
                  path="/songs/artist/:artist_name"
                  element={<ArtistSongs />}
                />
                <Route
                  path="/teases/artist/:artist_name"
                  element={<ArtistSongs />}
                />
                <Route
                  path="/feels/artist/:artist_name"
                  element={<ArtistFeels />}
                />
                <Route path="/songs/album/:id" element={<AlbumSongs />} />
                <Route path="/song/feels/:id" element={<FeelsForSong />} />
                <Route path="/tease/feels/:id" element={<FeelsForSong />} />
                <Route path="/song/:id" element={<SongDetails />} />

                {!isSongReadOnly && (
                  <>
                    <Route path="/edit-song/:id" element={<EditSong />} />
                    <Route path="/create-tease/:id" element={<CreateTease />} />
                    <Route path="/edit-tease/:id" element={<EditTease />} />
                  </>
                )}
                <Route path="/reports" element={<Reports />} />
                <Route path="/tease/:id" element={<TeaseDetails />} />
                <Route path="/albums/:artist_name" element={<Albums />} />
                <Route path="/data/:dash" element={<Data />} />
                <Route path="/feel/:id" element={<Feel />} />
                {!isFeelsReadOnly && (
                  <Route path="/feel/:id/export" element={<ExportFeel />} />
                )}
                {!isSongReadOnly && (
                  <Route
                    path="/song/:id/backdrop/:artist_name"
                    element={<Backdrop />}
                  />
                )}

                <Route
                  path="/organizations"
                  element={
                    <OnlyInternalRoute>
                      <Organizations />
                    </OnlyInternalRoute>
                  }
                />
                <Route
                  path="/organizations/:id"
                  element={
                    <OrganizationRoute>
                      <OrganizationDetails />
                    </OrganizationRoute>
                  }
                />
                <Route path="/organizations/my-teams" element={<MyTeams />} />
                <Route
                  path="/organizations/:id/teams"
                  element={
                    <OrganizationRoute>
                      <OrganizationTeams />
                    </OrganizationRoute>
                  }
                />
                <Route
                  path="/organizations/:id/artists"
                  element={
                    <OrganizationRoute>
                      <OrganizationArtists />
                    </OrganizationRoute>
                  }
                />
                <Route
                  path="/organizations/:id/members"
                  element={
                    <OrganizationRoute>
                      <OrganizationMembers />
                    </OrganizationRoute>
                  }
                />
                <Route
                  path="/organizations/:o_f_id/teams/:g_f_id"
                  element={
                    <OrganizationGroupRoute>
                      <TeamDetails />
                    </OrganizationGroupRoute>
                  }
                />
                <Route
                  path="/organizations/:o_f_id/teams/:g_f_id/members"
                  element={
                    <OrganizationGroupRoute>
                      <TeamDetailsMembers />
                    </OrganizationGroupRoute>
                  }
                />
                <Route
                  path="/organizations/:o_f_id/teams/:g_f_id/artists"
                  element={
                    <OrganizationGroupRoute>
                      <TeamDetailsArtists />
                    </OrganizationGroupRoute>
                  }
                />
                <Route path="/codes" element={<Codes />} />
                <Route
                  path="/faq"
                  element={<Navigate to="/dashboard" />}
                  render={() => {
                    window
                      .open('https://artists.feelsmusic.com/help', '_blank')
                      .focus()
                  }}
                />

                {!isFeelsFactoryReadOnly && (
                  <>
                    <Route
                      path="/create-new-feel-step-1/"
                      element={<CreateNewFeelStep1 />}
                    />
                    <Route
                      path="/create-new-feel-step-2/:id"
                      element={<Step2 />}
                    />
                    <Route
                      path="/create-new-feel-step-1/:artist_name"
                      element={<Step1 />}
                    />
                  </>
                )}
                <Route path="/trivia" element={<Trivia />} />
                <Route
                  path="/feels-drawer"
                  element={
                    <OnlyInternalRoute>
                      <FeelsDrawerPreview />
                    </OnlyInternalRoute>
                  }
                />
                <Route
                  path="/scream-room"
                  element={
                    <OnlyInternalRoute>
                      <ScreamRoom />
                    </OnlyInternalRoute>
                  }
                />
                {/* <Prompt
              when={useEditDataContext().isEditing}
              message="Are you sure you want to leave?"
            /> */}
              </Route>
              <Route path="/terms-of-service" element={<ExternalRedirect to="https://www.feelsmusic.com/terms-of-service" />} />
              <Route path="/privacy-policy" element={<ExternalRedirect to="https://www.feelsmusic.com/privacy-policy" />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        )}
      </div>
    </>
  )
}
